import { useToast } from "../../context/ToastContext";
import {
  Box,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Typography,
  makeStyles,
} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import CloseIcon from "@material-ui/icons/Close";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";

import MessageList from "./MessageList";
import ChatInput from "./ChatInput";
import IntakeList from "./IntakeList";
import { useIntakeChat } from "./hooks/useIntakeChat";
import { getUsername } from "../shared/helpers/helper";
import { EmptyState } from "../shared/helpers/StatePrimitives";
import { tokens } from "../../theme/tokens";

const useStyles = makeStyles(() => ({
  drawer: {
    width: 700,
    flexShrink: 0,
  },
  drawerPaper: {
    width: 700,
    backgroundColor: tokens.color.surface.sunken,
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    overflow: "hidden",
    borderTopLeftRadius: "16px",
    borderBottomLeftRadius: "16px",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 20px",
    backgroundColor: tokens.color.surface.default,
    color: tokens.color.text.primary,
    borderBottom: `1px solid ${tokens.color.border.default}`,
    flexShrink: 0,
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  headerIcon: {
    color: tokens.color.brand.primaryAction,
    fontSize: "1.25em",
  },
  headerTitle: {
    fontFamily: tokens.type.family,
    fontSize: "1em",
    fontWeight: 600,
    letterSpacing: "0.01em",
  },
  body: {
    display: "flex",
    flex: 1,
    minHeight: 0,
    overflow: "hidden",
  },
  intakePanel: {
    width: 200,
    flexShrink: 0,
  },
  chatPanel: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
  },
  messagesContainer: {
    flex: 1,
    overflowY: "auto",
    padding: "20px 16px 8px",
    backgroundColor: tokens.color.surface.default,
    minHeight: 0,
  },
  inputContainer: {
    padding: "12px 16px 16px",
    backgroundColor: tokens.color.surface.default,
    boxShadow: `0 -1px 0 ${tokens.color.border.default}`,
    flexShrink: 0,
  },
  emptyChat: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: 1,
    backgroundColor: tokens.color.surface.default,
  },
  loadingContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: 1,
    backgroundColor: tokens.color.surface.default,
  },
  errorToast: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    margin: "0 12px 10px 12px",
    padding: "10px 12px",
    backgroundColor: tokens.color.surface.sunken,
    border: `1px solid ${tokens.color.status.error}`,
    borderRadius: tokens.radius.sm,
    flexShrink: 0,
  },
  errorIcon: {
    color: tokens.color.status.error,
    fontSize: "1.1em",
    flexShrink: 0,
  },
  errorText: {
    flex: 1,
    fontFamily: tokens.type.family,
    fontSize: "0.8em",
    color: tokens.color.status.error,
    lineHeight: 1.4,
  },
  errorClose: {
    padding: 2,
    color: tokens.color.status.error,
    opacity: 0.7,
    "&:hover": { opacity: 1, backgroundColor: "transparent" },
  },
}));

interface AIChatDrawerProps {
  open: boolean;
  onClose: () => void;
}

const AIChatDrawer = ({ open, onClose }: AIChatDrawerProps) => {
  const classes = useStyles();
  const { showToast } = useToast();

  const userFullName = getUsername();

  const {
    intakes,
    activeIntakeId,
    messages,
    isTyping,
    loading,
    isCreating,
    error,
    clearError,
    selectIntake,
    sendMessage,
    createIntake,
    closeIntake,
    updateLabel,
    goBackToList,
  } = useIntakeChat();

  const handleDelete = async (id: string) => {
    await closeIntake(id);
    showToast("Chat deleted successfully", "success");
  };

  const errorToast = error && (
    <Box className={classes.errorToast}>
      <ErrorOutlineIcon className={classes.errorIcon} />
      <Typography className={classes.errorText}>{error}</Typography>
      <IconButton
        size="small"
        className={classes.errorClose}
        onClick={clearError}
        disableRipple
      >
        <CloseIcon style={{ fontSize: "0.95em" }} />
      </IconButton>
    </Box>
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      className={classes.drawer}
      classes={{ paper: classes.drawerPaper }}
    >
      {/* Header */}
      <Box className={classes.header}>
        <Box className={classes.headerLeft}>
          {activeIntakeId ? (
            <IconButton
              size="small"
              onClick={goBackToList}
              style={{ color: tokens.color.text.secondary }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          ) : (
            <SmartToyOutlinedIcon className={classes.headerIcon} />
          )}
          <Typography className={classes.headerTitle}>
            AI Case Assistant
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={onClose}
          style={{ color: tokens.color.text.secondary }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Body: intake list + chat */}
      <Box className={classes.body}>
        {/* Left panel — Intake list */}
        <Box className={classes.intakePanel}>
          <IntakeList
            intakes={intakes}
            activeIntakeId={activeIntakeId}
            onSelect={selectIntake}
            onCreate={createIntake}
            onClose={handleDelete}
            onRename={updateLabel}
            isCreating={isCreating}
          />
        </Box>

        <Divider orientation="vertical" flexItem />

        {/* Right panel — Chat area */}
        <Box className={classes.chatPanel}>
          {loading ? (
            <Box className={classes.loadingContainer}>
              <CircularProgress
                size={28}
                style={{ color: tokens.color.text.secondary }}
              />
            </Box>
          ) : activeIntakeId ? (
            <>
              <Box className={classes.messagesContainer}>
                <MessageList
                  messages={messages}
                  isTyping={isTyping}
                  userFullName={userFullName}
                />
              </Box>
              {errorToast}
              <Box className={classes.inputContainer}>
                <ChatInput onSend={sendMessage} disabled={isTyping} />
              </Box>
            </>
          ) : (
            <>
              <Box className={classes.emptyChat}>
                <EmptyState
                  icon={<ForumOutlinedIcon />}
                  title="Start a conversation"
                  description="Select a chat on the left or start a new one to ask the assistant about a case."
                />
              </Box>
              {errorToast}
              <Box className={classes.inputContainer}>
                <ChatInput onSend={sendMessage} disabled={isTyping} />
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default AIChatDrawer;
